import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { Column } from 'react-table';

import {
  getSubscriptionPrice,
} from '../../../../shared/subscriptions';
import InternalToolingStore from '../../../redux/internal_tooling';
import SelectableReactTable from '../../generic/SelectableReactTable';
import type { Organization } from '../../../../shared/model-typings/organization';

type FilteredTableProps = {
  isSearchOnly: boolean;
};

const FilteredOrgTable: VoidFunctionComponent<FilteredTableProps> = ({ isSearchOnly }) => {
  // Setup Redux
  const dispatch = useDispatch();
  const orgList = useSelector(InternalToolingStore.selectors.getFilteredOrgs);
  const orgLoadingStatus = useSelector(InternalToolingStore.selectors.getOrgLoadingStatus);
  const selectedOrg = useSelector(InternalToolingStore.selectors.getSelectedOrg);

  const selectOrg = useCallback((org: Organization) => {
    if (selectedOrg && selectedOrg.id === org.id) {
      dispatch(InternalToolingStore.actions.setSelectedOrg(null));
    } else {
      dispatch(InternalToolingStore.actions.setSelectedOrg(org));
    }
  }, [dispatch, selectedOrg]);

  const columns: Array<Partial<Column>> = useMemo(() => {
    let cols: Array<Partial<Column>> = [
      {
        Header: 'Title',
        accessor: 'title',
      },
      {
        Header: 'Org ID',
        accessor: 'id',
      },
    ];

    if (!isSearchOnly) {
      cols = cols.concat([
        {
          Header: 'Access Level',
          accessor: 'access_level',
        },
        {
          Header: 'Stripe Plan (per dev * month)',
          accessor: 'stripePlan',
        },
        {
          Header: 'Contributor Count',
          accessor: 'contributor_count',
        },
        {
          Header: 'Project Count',
          accessor: 'projectCount',
        },
        {
          Header: 'Custom Billing',
          accessor: 'customBilling',
        },
      ]);
    }
    return cols;
  }, [isSearchOnly]);

  const tableData = useMemo(() => {
    return orgList.map((org) => {
      const stripePlan = `$${getSubscriptionPrice(org.stripe_plan)}`;

      const customBilling = org.custom_billing ? 'yes' : 'no';

      return { ...org, customBilling, stripePlan };
    });
  }, [orgList]);

  return (
    // @ts-expect-error There are so many props in the table type that I don't think it's reasoable to fulfill them all.
    <SelectableReactTable
      // convert stripe plan values to be something that's human readble
      data={tableData}
      loading={orgLoadingStatus.loading}
      loadingText="fetching org data"
      showPagination
      showPaginationTop
      showPaginationBottom={false}
      defaultPageSize={10}
      className="-striped -highlight"
      columns={columns}
      selectedData={selectedOrg}
      onSelect={selectOrg}
      areEqual={(orgA, orgB) => orgA.id === orgB.id}
      selectedBackgroundColor="#00afec"
      selectedTextColor="black"
    />
  );
};

export default FilteredOrgTable;
